import { createContext, useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { loginUser } from "../Services/authService";
import { useTranslation } from "../i18n/LanguageContext";
import { fetchMyProfile } from "../Services/apiService";
import socketService from "../Services/socketService";
import {
  getToken as readToken,
  getUser as readUser,
  saveSession as persistSession,
  saveUser as persistUser,
  clearSession as clearPersistedSession,
  markJustLoggedIn,
  markTwoFactorPrompt,
  SESSION_UPDATED_EVENT,
} from "../Services/sessionStorage";

export const AuthContext = createContext();

function readStoredSession() {
  const token = readToken();
  const user = readUser();
  if (!token || !user) return { token: null, user: null };
  return { token, user };
}

// GET /profile/me returns snake_case column names; login/register/refresh
// return the camelCase shape from routes/Auth.js's userPayload(). Normalize
// to that same camelCase shape so anything reading `user.languageCode` (the
// language switcher, the language auto-apply effect) works regardless of
// which of the two ever last populated it.
function mapProfileToUserFields(profile) {
  return {
    id: profile.id,
    email: profile.email,
    name: profile.name,
    roles: profile.roles,
    countryId: profile.country_id,
    countryCode: profile.country_code,
    countryName: profile.country_name,
    languageId: profile.language_id,
    languageCode: profile.language_code,
    languageName: profile.language_name,
    twoFactorEnabled: profile.two_factor_enabled ?? null,
  };
}

export const AuthProvider = ({ children }) => {
  // Read the stored session synchronously so roles are known on the very first
  // render — the portal guards (and PortalProvider's stored active portal)
  // depend on them; an empty first render would bounce a reload to /dashboard.
  const [initialSession] = useState(readStoredSession);
  const [token, setToken] = useState(initialSession.token);
  const [user, setUser] = useState(initialSession.user);
  const loading = false;
  const { t } = useTranslation();

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // A token refresh (see apiFetch.js) brings back the account's current roles.
  // Apply them right away so the portal switcher and route guards follow: a
  // removed role takes its portal/pages away (PortalProvider + PortalRoute move
  // the user to a dashboard they can still use), a granted one shows up.
  useEffect(() => {
    function onSessionUpdated(event) {
      const fresh = event.detail;
      const prev = userRef.current;
      if (!fresh || !prev) return;

      const nextRoles = Object.values(fresh.roles || {});
      const lostRole = Object.values(prev.roles || {}).some((r) => !nextRoles.includes(r));

      const merged = { ...prev, ...fresh };
      persistUser(merged);
      setToken(readToken());
      setUser(merged);
      if (lostRole) {
        toast.info(t("session.accessChanged"), { toastId: "access-changed" });
      }
    }

    window.addEventListener(SESSION_UPDATED_EVENT, onSessionUpdated);
    return () => window.removeEventListener(SESSION_UPDATED_EVENT, onSessionUpdated);
  }, [t]);

  useEffect(() => {
    // Refresh from the backend once on load — the cached blob is a snapshot
    // from whenever the session started, and (for sessions cached before
    // this feature shipped) may not even have language/country fields yet.
    if (initialSession.token) {
      fetchMyProfile()
        .then((res) => {
          if (!res?.data) return;
          setUser((prev) => {
            const merged = { ...prev, ...mapProfileToUserFields(res.data) };
            persistUser(merged);
            return merged;
          });
        })
        .catch(() => {
          // Stale/expired token — leave the cached session as-is; the next
          // authenticated request will hit the normal 401/refresh handling.
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only refresh
  }, []);

  // Shared by login and by any flow that already has a session from the
  // backend (register/complete also auto-logs-in and returns the same shape).
  const applySession = useCallback((data) => {
    persistSession(data.token, data.refreshToken, data.user);
    setToken(data.token);
    setUser(data.user);
    markJustLoggedIn();
    markTwoFactorPrompt();
    return data.user;
  }, []);

  // Patches specific fields (e.g. after saving a new language preference)
  // into both the in-memory user and the persisted session, without a full
  // re-login.
  const updateUserFields = useCallback((partial) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...partial };
      persistUser(merged);
      return merged;
    });
  }, []);

  // Admin/operator accounts get a session back immediately. Patient/therapist
  // accounts come back with { mfaRequired: true, email } instead - no tokens
  // yet, so don't persist a session until the OTP step completes it.
  const login = async (email, password) => {
    const data = await loginUser(email, password);
    if (data?.mfaRequired) {
      return { mfaRequired: true, email: data.email || email };
    }
    return { user: applySession(data) };
  };

  const logout = () => {
    socketService.disconnect();
    clearPersistedSession();
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = !!token;
  const roles = useMemo(() => (user ? Object.values(user.roles || {}) : []), [user]);
  const hasRole = (role) => roles.includes(role);
  const isAdmin = hasRole("admin");

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        roles,
        hasRole,
        isAdmin,
        loading,
        login,
        applySession,
        updateUserFields,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
